import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { alpha, Box, useTheme } from '@mui/material';
import { grey } from '@mui/material/colors';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { HIDE_GRACE_MS, IDLE_COLLAPSE_MS, IDLE_POLL_MS, SLOT_HEIGHT } from '../../../constants/ui';
import { useFabContext } from '../../../contexts/FabContext';
import { useFooterContext } from '../../../contexts/FooterContext';
import { useIsLongHero } from '../../../hooks/useIsLongHero';
import { useLogoScale } from '../../../hooks/useLogoScale';
import { prefersReducedMotion, useScrollGuidance } from '../../../hooks/useScrollGuidance';
import { selectContentBackgroundColor } from '../../../store/selectors/derived';
import { DomEventEnum, FabFaceEnum } from '../../../types/shell';

// Mobile nav banner — replaces the FAB on small screens, in a dual-edge sticky slot that
// collapses only after scrolling settles.
export const NavBar = () => {
  const theme = useTheme();
  const { anchorRef, heroRef } = useFooterContext();
  const { fabConfig } = useFabContext();
  const contentBgColorOverride = useSelector(selectContentBackgroundColor);
  const slotBgColor = contentBgColorOverride || grey[100];
  // Tracks the app bar so the slot stays flush under it when the logo scale grows the bar.
  const { headerHeight } = useLogoScale();

  // The down face is gated on a "long" hero (else the selection is already on screen and the
  // helper is noise) — but a custom down target opts out of that gate.
  const hasCustomDownTarget = !!fabConfig.downAnchorRef || !!fabConfig.downThresholds;

  const isLongHero = useIsLongHero(heroRef);

  const { fabState, suppressUntilSettle } = useScrollGuidance({
    anchorRef,
    downEnabled: !!fabConfig.down && (hasCustomDownTarget || isLongHero),
    upEnabled: !!fabConfig.up,
    mobileDownThresholds: true,
    downAnchorRef: fabConfig.downAnchorRef,
    downThresholds: fabConfig.downThresholds,
    upThresholds: fabConfig.upThresholds,
    downHideAt: fabConfig.downHideAt,
    downIgnoresBar: fabConfig.downIgnoresBar,
  });

  // `shown`/`face` diverge from the raw `fabState` via the hide-grace timer below; the slot
  // collapses separately (only once scrolling is idle) to avoid a mid-scroll layout shift.
  const [shown, setShown] = useState(false);
  const [face, setFace] = useState<FabFaceEnum.Down | FabFaceEnum.Up>(FabFaceEnum.Down);
  const [slotExpanded, setSlotExpanded] = useState(false);

  // Lets the effect below read `shown` without depending on it (it must react to `fabState`,
  // not to its own `shown` writes).
  const shownRef = useRef(shown);
  shownRef.current = shown;

  const hideTimerRef = useRef<number | null>(null);
  const idleTimerRef = useRef<number | null>(null);
  const lastScrollTRef = useRef(0);

  const clearHideTimer = useCallback(() => {
    if (hideTimerRef.current !== null) {
      window.clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const clearIdleTimer = useCallback(() => {
    if (idleTimerRef.current !== null) {
      window.clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  }, []);

  const collapseWhenIdle = useCallback(() => {
    clearIdleTimer();
    const check = () => {
      const since = performance.now() - lastScrollTRef.current;
      if (since < IDLE_COLLAPSE_MS) {
        idleTimerRef.current = window.setTimeout(check, IDLE_POLL_MS - since);

        return;
      }
      idleTimerRef.current = null;
      setSlotExpanded(false);
    };
    idleTimerRef.current = window.setTimeout(check, IDLE_POLL_MS);
  }, [clearIdleTimer]);

  useEffect(() => {
    const visible = fabState !== FabFaceEnum.Hidden;

    if (visible) {
      clearHideTimer();
      clearIdleTimer();
      setFace(fabState === FabFaceEnum.Up ? FabFaceEnum.Up : FabFaceEnum.Down);
      setShown(true);
      setSlotExpanded(true);

      return;
    }

    // Hide after a grace period, then defer the slot collapse until scrolling is idle.
    if (shownRef.current && hideTimerRef.current === null) {
      hideTimerRef.current = window.setTimeout(() => {
        hideTimerRef.current = null;
        setShown(false);
        collapseWhenIdle();
      }, HIDE_GRACE_MS);
    }
  }, [fabState, clearHideTimer, clearIdleTimer, collapseWhenIdle]);

  // Track scroll timestamps for the idle-collapse poll (capture-phase so it works inside an
  // iframe/embed scroller).
  useEffect(() => {
    const onScroll = () => {
      lastScrollTRef.current = performance.now();
    };
    document.addEventListener(DomEventEnum.Scroll, onScroll, { passive: true, capture: true });

    return () => document.removeEventListener(DomEventEnum.Scroll, onScroll, { capture: true });
  }, []);

  useEffect(
    () => () => {
      clearHideTimer();
      clearIdleTimer();
    },
    [clearHideTimer, clearIdleTimer],
  );

  const faceConfig = face === FabFaceEnum.Up ? fabConfig.up : fabConfig.down;

  const handleClick = useCallback(() => {
    if (!faceConfig) {
      return;
    }

    const behavior: ScrollBehavior = prefersReducedMotion() ? 'auto' : 'smooth';

    // Scroll first, then suppress; hiding before the scroll raced it and could swallow the scroll.
    suppressUntilSettle();
    faceConfig.onActivate(behavior);
  }, [faceConfig, suppressUntilSettle]);

  return (
    <Box
      data-testid="nav-slot"
      sx={{
        position: 'sticky',
        top: headerHeight,
        bottom: 0,
        zIndex: 19,
        height: slotExpanded ? SLOT_HEIGHT : 0,
        overflow: 'hidden',
        transition: 'height 200ms cubic-bezier(0.4, 0, 0.2, 1)',
        backgroundColor: slotBgColor,
      }}
    >
      <Box
        component="button"
        type="button"
        onClick={handleClick}
        aria-hidden={!shown}
        aria-label={faceConfig?.bannerLabel ?? faceConfig?.label}
        disabled={!shown}
        sx={{
          width: '100%',
          height: SLOT_HEIGHT,
          padding: '4px 5px',
          border: 'none',
          cursor: shown ? 'pointer' : 'default',
          backgroundColor: alpha(theme.palette.primary.main, 0.08),
          '&:active': {
            backgroundColor: alpha(theme.palette.primary.main, 0.12),
          },
          opacity: shown ? 1 : 0,
          pointerEvents: shown ? 'auto' : 'none',
          transition: 'opacity 200ms cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
        }}
      >
        {[FabFaceEnum.Down, FabFaceEnum.Up].map((f) => {
          const cfg = f === FabFaceEnum.Up ? fabConfig.up : fabConfig.down;
          if (!cfg) {
            return null;
          }

          return (
            <Box
              key={f}
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                fontWeight: 500,
                fontSize: 14,
                lineHeight: '24px',
                color: 'primary.main',
                opacity: face === f ? 1 : 0,
                transition: 'opacity 120ms cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              {cfg.bannerLabel ?? cfg.label}
              {f === FabFaceEnum.Up ? (
                <ArrowUpwardIcon sx={{ fontSize: 18 }} />
              ) : (
                <ArrowDownwardIcon sx={{ fontSize: 18 }} />
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
